import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgAccess } from "@/hooks/useOrgAccess";
import { useCancelOrgDeletion } from "@/hooks/useOrgDeletion";

/** Show the soft warning from day 25 of a 30-day trial, i.e. 5 days out. */
const WARN_FROM_DAYS_LEFT = 5;

/**
 * Trial state banner.
 *
 * Silent while the trial has time left or a paid subscription is active, so it
 * never nags a paying customer. Deliberately states up front that the trial
 * runs on Pro: someone who later picks Starter should learn that here, not by
 * discovering a missing feature after paying.
 */
export function TrialBanner() {
  const { data: org } = useOrganization();
  const { data: access } = useOrgAccess(org?.organizationId);
  const cancelDeletion = useCancelOrgDeletion();

  if (!access) return null;

  if (access.state === "pending_deletion") {
    const dateLabel = access.deletionScheduledAt
      ? new Date(access.deletionScheduledAt).toLocaleDateString("pl-PL")
      : null;

    const handleCancel = () => {
      if (!org?.organizationId) return;
      cancelDeletion.mutate(
        { organizationId: org.organizationId },
        {
          onSuccess: () => toast.success("Usunięcie anulowane. Dostęp przywrócony."),
          onError: (e) => toast.error(e instanceof Error ? e.message : "Nie udało się anulować"),
        },
      );
    };

    return (
      <div className="border-b border-border bg-danger-bg px-4 py-3 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-navy">
            <strong className="font-semibold">
              Organizacja zostanie usunięta{dateLabel ? ` ${dateLabel}` : ""}.
            </strong>{" "}
            Do&nbsp;tego czasu dane są&nbsp;w&nbsp;trybie tylko do&nbsp;odczytu. Możesz
            je&nbsp;jeszcze wyeksportować albo anulować usunięcie.
          </p>
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelDeletion.isPending}
            className="shrink-0 rounded-lg bg-navy px-4 py-2 text-center text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-50"
          >
            {cancelDeletion.isPending ? "Anuluję…" : "Anuluj usunięcie"}
          </button>
        </div>
      </div>
    );
  }

  if (access.state === "expired") {
    return (
      <div className="border-b border-border bg-danger-bg px-4 py-3 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-navy">
            <strong className="font-semibold">Okres próbny minął.</strong> Twoje dane są bezpieczne
            i&nbsp;nadal je&nbsp;widzisz, ale edycja jest wstrzymana. Wybierz plan, żeby wrócić
            do&nbsp;pracy.
          </p>
          <Link
            to="/cennik"
            className="shrink-0 rounded-lg bg-navy px-4 py-2 text-center text-sm font-semibold text-white hover:bg-navy-dark"
          >
            Wybierz plan
          </Link>
        </div>
      </div>
    );
  }

  if (
    access.state === "trial" &&
    access.daysLeft !== null &&
    access.daysLeft <= WARN_FROM_DAYS_LEFT
  ) {
    const dni = access.daysLeft === 1 ? "1 dzień" : `${access.daysLeft} dni`;

    return (
      <div className="border-b border-border bg-warn-bg px-4 py-3 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-navy">
            <strong className="font-semibold">Okres próbny kończy się za {dni}.</strong> Testujesz
            Stockuj w&nbsp;planie Pro, więc po&nbsp;jego zakończeniu wybierz plan dopasowany
            do&nbsp;potrzeb: w&nbsp;Starterze część funkcji Pro nie&nbsp;będzie dostępna.
          </p>
          <Link
            to="/cennik"
            className="shrink-0 rounded-lg bg-navy px-4 py-2 text-center text-sm font-semibold text-white hover:bg-navy-dark"
          >
            Zobacz plany
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
